<script setup lang="ts">
import { toRef } from 'vue'
import { useField } from 'vee-validate'
import InputField from '@/components/InputField.vue'
import FormError from '@/components/FormError.vue'

const props = withDefaults(
  defineProps<{
    type: 'text' | 'password' | 'num' | 'ip' | 'email'
    value?: string
    name: string
    label: string
    successMessage?: string
    placeholder?: string
    autocomplete?: string
    disabled?: boolean
    transparent?: boolean
    precision?: number
    allowNegative?: boolean
    allowMultipleZeros?: boolean
    multipleErrors?: boolean
  }>(),
  {
    value: '',
  },
)

// use `toRef` to create reactive references to `name` prop which is passed to `useField`
// this is important because vee-validte needs to know if the field name changes
// https://vee-validate.logaretm.com/v4/guide/composition-api/caveats
const name = toRef(props, 'name')

// we don't provide any rules here because we are using form-level validation
// https://vee-validate.logaretm.com/v4/guide/validation#form-level-validation
const {
  value: inputValue,
  errorMessage,
  handleBlur,
  handleChange,
  meta,
  errors,
} = useField(name, undefined, {
  initialValue: props.value,
  bails: false,
})
</script>

<template>
  <div class="text-input" :class="{ 'has-error': !!errorMessage, success: meta.valid }">
    <InputField
      :name="name"
      :id="name"
      :type="type"
      :precision="precision"
      :allow-negative="allowNegative"
      :allow-multiple-zeros="allowMultipleZeros"
      :placeholder="placeholder"
      :autocomplete="autocomplete"
      :disabled="disabled"
      :transparent="transparent"
      :error="!!errorMessage"
      v-model:model-value="inputValue"
      @update:model-value="handleChange"
      @blur="handleBlur"
    >
      {{ label }}
    </InputField>
    <template v-if="multipleErrors">
      <FormError v-for="(error, index) in errors" :key="index">{{ error }}</FormError>
    </template>
    <FormError v-if="(!multipleErrors && !!errorMessage) || (meta.valid && successMessage)">{{ errorMessage || successMessage }}</FormError>
  </div>
</template>

<style scoped>
.text-input {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
</style>
